import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Calendar, ChevronLeft, ChevronRight, Plus, Clock, Umbrella, Coffee, X, Check, History, PlusCircle } from 'lucide-react';
import { toast } from 'sonner';
import { safeFixed } from '../utils/formatters';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MONTHS_FR = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 
                   'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

const LEAVE_TYPES = [
  { value: 'CONGE', label: 'Congé payé', color: 'bg-green-500', textColor: 'text-green-700' },
  { value: 'REPOS', label: 'Repos', color: 'bg-blue-500', textColor: 'text-blue-700' },
  { value: 'RECUP', label: 'Récupération', color: 'bg-amber-500', textColor: 'text-amber-700' },
  { value: 'MALADIE', label: 'Maladie', color: 'bg-red-500', textColor: 'text-red-700' },
  { value: 'SANS_SOLDE', label: 'Sans solde', color: 'bg-gray-400', textColor: 'text-gray-600' }
];

const getLeaveColor = (type) => LEAVE_TYPES.find(t => t.value === type)?.color || 'bg-gray-400';
const getLeaveTextColor = (type) => LEAVE_TYPES.find(t => t.value === type)?.textColor || 'text-gray-600';

export default function EmployeePlanning({ employeeId, employeeName, isOwner = false, onClose }) {
  const [planning, setPlanning] = useState(null);
  const [loading, setLoading] = useState(true);
  const [balanceLogs, setBalanceLogs] = useState([]);
  const [viewMode, setViewMode] = useState('months'); // 'months' or 'list'
  const [showAddLeave, setShowAddLeave] = useState(false);
  const [showQuickRest, setShowQuickRest] = useState(false);
  const [showCreditDialog, setShowCreditDialog] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [leaveForm, setLeaveForm] = useState({
    type: 'CONGE',
    start_date: '',
    end_date: '',
    reason: ''
  });
  const [creditForm, setCreditForm] = useState({
    balance_type: 'LEAVE',
    amount: 1,
    reason: '',
    reference_date: new Date().toISOString().split('T')[0]
  });
  const [showRestDatePicker, setShowRestDatePicker] = useState(false);
  const [restDate, setRestDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    fetchPlanning();
    fetchBalanceLogs();
  }, [employeeId]);

  const fetchPlanning = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/employees/${employeeId}/planning`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setPlanning(data);
    } catch (error) {
      toast.error('Erreur lors du chargement du planning');
    } finally {
      setLoading(false);
    }
  };

  const fetchBalanceLogs = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/employees/${employeeId}/balance-logs?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setBalanceLogs(data);
      }
    } catch (error) {
      console.error('Error fetching balance logs:', error);
    }
  };

  const handleAddLeave = async () => {
    if (!leaveForm.start_date || !leaveForm.end_date) {
      toast.error('Dates requises');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/employees/${employeeId}/leaves`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(leaveForm)
      });
      const data = await res.json();
      if (data.error) {
        toast.error(data.error);
      } else {
        toast.success('Congé ajouté');
        setShowAddLeave(false);
        setLeaveForm({ type: 'CONGE', start_date: '', end_date: '', reason: '' });
        // Update balances directly from response and refetch planning/logs in parallel
        if (data.balances) {
          setPlanning(prev => prev ? { ...prev, balances: data.balances } : prev);
        }
        // Refetch planning and logs for full data update
        await Promise.all([fetchPlanning(), fetchBalanceLogs()]);
      }
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  // Add credit to balance (congés, repos, récup)
  const handleAddCredit = async () => {
    if (!creditForm.amount || !creditForm.reason) {
      toast.error('Montant et raison requis');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/employees/${employeeId}/balance-credit`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(creditForm)
      });
      const data = await res.json();
      if (data.error) {
        toast.error(data.error);
      } else {
        toast.success(`Crédit ajouté: +${creditForm.amount} ${creditForm.balance_type === 'RECOVERY' ? 'heure(s)' : 'jour(s)'}`);
        setShowCreditDialog(false);
        setCreditForm({ balance_type: 'LEAVE', amount: 1, reason: '', reference_date: new Date().toISOString().split('T')[0] });
        // Update balances directly from response
        if (data.balances) {
          setPlanning(prev => prev ? { ...prev, balances: data.balances } : prev);
        }
        // Refetch planning and logs for full data update
        await Promise.all([fetchPlanning(), fetchBalanceLogs()]);
      }
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  // Quick request rest (simpler than handleQuickAddRest)
  const handleRequestRest = async (date, type = 'REPOS') => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/employees/${employeeId}/request-rest`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ date, type })
      });
      const data = await res.json();
      if (data.error) {
        toast.error(data.error);
      } else {
        toast.success(`${type === 'REPOS' ? 'Repos' : 'Congé'} posé le ${date}`);
        setShowQuickRest(false);
        setSelectedWeek(null);
        // Update balances directly from response
        if (data.balances) {
          setPlanning(prev => prev ? { ...prev, balances: data.balances } : prev);
        }
        // Refetch planning and logs for full data update
        await Promise.all([fetchPlanning(), fetchBalanceLogs()]);
      }
    } catch (error) {
      toast.error('Erreur lors de la pose');
    }
  };

  // Quick add rest day for a specific week
  const handleQuickAddRest = async (weekStart, restDate) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/employees/${employeeId}/rest-days`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          week_start: weekStart,
          scheduled_date: restDate,
          notes: 'Repos hebdomadaire'
        })
      });
      const data = await res.json();
      if (data.error) {
        toast.error(data.error);
      } else {
        toast.success('Jour de repos ajouté');
        setShowQuickRest(false);
        setSelectedWeek(null);
        fetchPlanning();
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  // Cancel event (leave or rest day)
  const handleCancelEvent = async (event) => {
    if (!window.confirm(`Voulez-vous annuler ce ${event.type === 'REPOS' ? 'repos' : 'congé'} ?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      
      if (event.type === 'REPOS' && event.restDayId) {
        // Cancel rest day
        const res = await fetch(`${API_URL}/api/employees/${employeeId}/rest-days/${event.restDayId}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (data.error) {
          toast.error(data.error);
        } else {
          toast.success('Repos annulé');
          // Update balances directly from response
          if (data.balances) {
            setPlanning(prev => prev ? { ...prev, balances: data.balances } : prev);
          }
          // Refetch planning and logs for full data update
          await Promise.all([fetchPlanning(), fetchBalanceLogs()]);
        }
      } else if (event.leaveId) {
        // Cancel leave
        const res = await fetch(`${API_URL}/api/employees/leaves/${event.leaveId}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (data.error) {
          toast.error(data.error);
        } else {
          toast.success('Congé annulé');
          // Update balances directly from response
          if (data.balances) {
            setPlanning(prev => prev ? { ...prev, balances: data.balances } : prev);
          }
          // Refetch planning and logs for full data update
          await Promise.all([fetchPlanning(), fetchBalanceLogs()]);
        }
      } else {
        toast.error('Impossible d\'identifier l\'événement à annuler');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'annulation');
    }
  };

  // Generate weeks for the 6-month period
  const getWeeks = () => {
    if (!planning) return [];
    const start = new Date(planning.period.start);
    const end = new Date(planning.period.end);
    const weeks = [];
    
    let current = new Date(start);
    // Go to Monday of the first week
    const day = current.getDay();
    current.setDate(current.getDate() - (day === 0 ? 6 : day - 1));
    
    while (current <= end) {
      const weekStart = new Date(current);
      const weekEnd = new Date(current);
      weekEnd.setDate(weekEnd.getDate() + 6);
      
      // Get events for this week
      const weekEvents = (planning.events || []).filter(e => {
        const eStart = new Date(e.start);
        const eEnd = new Date(e.end);
        return (eStart <= weekEnd && eEnd >= weekStart);
      });
      
      // Generate days for this week
      const days = [];
      for (let i = 0; i < 7; i++) {
        const d = new Date(weekStart);
        d.setDate(d.getDate() + i);
        const dateStr = d.toISOString().split('T')[0];
        const dayEvents = weekEvents.filter(e => dateStr >= e.start && dateStr <= e.end);
        days.push({
          date: d,
          dateStr,
          events: dayEvents,
          isToday: dateStr === new Date().toISOString().split('T')[0]
        });
      }
      
      weeks.push({
        start: weekStart,
        end: weekEnd,
        startStr: weekStart.toISOString().split('T')[0],
        days,
        events: weekEvents,
        hasRest: weekEvents.some(e => e.type === 'REPOS')
      });
      
      current.setDate(current.getDate() + 7);
    }
    
    return weeks;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  if (loading) {
    return <div className="p-8 text-center">Chargement...</div>;
  }

  const weeks = getWeeks();

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold">{employeeName || planning?.employee?.name}</h2>
          <p className="text-muted-foreground">{planning?.employee?.position}</p>
        </div>
        {onClose && (
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        )}
      </div>

      {/* Soldes - Plus compact */}
      <div className="flex gap-4 flex-wrap">
        <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-50 border border-green-200">
          <Umbrella className="h-5 w-5 text-green-600" />
          <div>
            <span className="text-xl font-bold text-green-700">{safeFixed(planning?.balances?.leave, 1)}</span>
            <span className="text-sm text-green-600 ml-1">jours congés</span>
          </div>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-50 border border-amber-200">
          <Clock className="h-5 w-5 text-amber-600" />
          <div>
            <span className="text-xl font-bold text-amber-700">{safeFixed(planning?.balances?.recovery, 1)}</span>
            <span className="text-sm text-amber-600 ml-1">h récup</span>
          </div>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-50 border border-blue-200">
          <Coffee className="h-5 w-5 text-blue-600" />
          <div>
            <span className="text-xl font-bold text-blue-700">{planning?.balances?.restDaysOwed || 0}</span>
            <span className="text-sm text-blue-600 ml-1">repos à poser</span>
          </div>
        </div>
      </div>

      {/* Actions (Owner only) */}
      {isOwner && (
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => setShowAddLeave(true)} data-testid="add-leave-btn">
            <Plus className="h-4 w-4 mr-2" />
            Congé / Absence
          </Button>
          <Button variant="outline" onClick={() => setShowCreditDialog(true)} data-testid="add-credit-btn">
            <PlusCircle className="h-4 w-4 mr-2" />
            Crédit manuel
          </Button>
          <Button 
            variant="outline" 
            className="border-blue-300 text-blue-700 hover:bg-blue-50"
            onClick={() => setShowRestDatePicker(true)} 
            data-testid="quick-rest-btn"
            disabled={!planning?.balances?.restDaysOwed || planning.balances.restDaysOwed < 1}
          >
            <Coffee className="h-4 w-4 mr-2" />
            Poser 1 jour de repos
          </Button>
        </div>
      )}

      {/* Planning Calendar - Liste par semaine */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Planning sur 6 mois
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {/* Header jours */}
          <div className="grid grid-cols-[120px_repeat(7,1fr)_80px] gap-px bg-muted border-b sticky top-0 z-10">
            <div className="bg-background p-2 text-sm font-medium text-muted-foreground">Semaine</div>
            {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(d => (
              <div key={d} className="bg-background p-2 text-sm font-medium text-center text-muted-foreground">{d}</div>
            ))}
            <div className="bg-background p-2 text-sm font-medium text-center text-muted-foreground">Repos</div>
          </div>
          
          {/* Weeks */}
          <div className="max-h-[500px] overflow-y-auto">
            {weeks.map((week, idx) => {
              const monthName = MONTHS_FR[week.start.getMonth()];
              const showMonth = idx === 0 || weeks[idx - 1].start.getMonth() !== week.start.getMonth();
              
              return (
                <div key={week.startStr}>
                  {/* Month separator */}
                  {showMonth && (
                    <div className="bg-muted/50 px-3 py-1 text-sm font-semibold text-primary border-b">
                      {monthName} {week.start.getFullYear()}
                    </div>
                  )}
                  
                  <div className="grid grid-cols-[120px_repeat(7,1fr)_80px] gap-px bg-muted">
                    {/* Week label */}
                    <div className="bg-background p-2 text-xs">
                      <span className="font-medium">{formatDate(week.start)}</span>
                      <span className="text-muted-foreground"> → </span>
                      <span className="font-medium">{formatDate(week.end)}</span>
                    </div>
                    
                    {/* Days */}
                    {week.days.map((day) => {
                      const hasEvent = day.events.length > 0;
                      const mainEvent = day.events[0];
                      
                      return (
                        <div
                          key={day.dateStr}
                          className={`
                            bg-background p-1 min-h-[40px] text-center relative
                            ${day.isToday ? 'ring-2 ring-primary ring-inset' : ''}
                            ${hasEvent ? getLeaveColor(mainEvent.type).replace('bg-', 'bg-') + '/20' : ''}
                          `}
                        >
                          <span className={`text-xs ${day.isToday ? 'font-bold text-primary' : 'text-muted-foreground'}`}>
                            {day.date.getDate()}
                          </span>
                          {hasEvent && (
                            <div className={`text-[10px] font-medium ${getLeaveTextColor(mainEvent.type)} truncate px-1`}>
                              {mainEvent.title}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    
                    {/* Rest day action */}
                    <div className="bg-background p-1 flex items-center justify-center">
                      {week.hasRest ? (
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 text-xs">
                          <Check className="h-3 w-3 mr-1" />
                          Posé
                        </Badge>
                      ) : isOwner ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 text-xs text-blue-600 hover:bg-blue-50"
                          onClick={() => {
                            setSelectedWeek(week);
                            setShowQuickRest(true);
                          }}
                          data-testid={`add-rest-${week.startStr}`}
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Repos
                        </Button>
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-sm">
        {LEAVE_TYPES.map(t => (
          <div key={t.value} className="flex items-center gap-1.5">
            <div className={`w-3 h-3 rounded ${t.color}`} />
            <span className="text-muted-foreground">{t.label}</span>
          </div>
        ))}
      </div>

      {/* Journal des soldes */}
      {balanceLogs.length > 0 && (
        <Card data-testid="balance-logs-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <History className="h-5 w-5" />
              Journal des mouvements
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">Date</th>
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">Type</th>
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">Opération</th>
                    <th className="text-right py-2 px-2 font-medium text-muted-foreground">Montant</th>
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">Raison</th>
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">Par</th>
                  </tr>
                </thead>
                <tbody>
                  {balanceLogs.map(log => (
                    <tr key={log.id} className="border-b last:border-0 hover:bg-muted/50">
                      <td className="py-2 px-2 text-muted-foreground">
                        {new Date(log.createdAt).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                      </td>
                      <td className="py-2 px-2">
                        <Badge variant="outline" className={
                          log.balance_type === 'LEAVE' ? 'bg-green-50 text-green-700' :
                          log.balance_type === 'REST' ? 'bg-blue-50 text-blue-700' :
                          'bg-amber-50 text-amber-700'
                        }>
                          {log.balance_type === 'LEAVE' ? 'Congé' : log.balance_type === 'REST' ? 'Repos' : 'Récup'}
                        </Badge>
                      </td>
                      <td className="py-2 px-2">
                        <span className={log.operation === 'CREDIT' ? 'text-green-600' : 'text-red-600'}>
                          {log.operation === 'CREDIT' ? '+ Crédit' : '- Débit'}
                        </span>
                      </td>
                      <td className="py-2 px-2 text-right font-medium">
                        <span className={log.operation === 'CREDIT' ? 'text-green-600' : 'text-red-600'}>
                          {log.operation === 'CREDIT' ? '+' : '-'}{log.amount} {log.balance_type === 'RECOVERY' ? 'h' : 'j'}
                        </span>
                      </td>
                      <td className="py-2 px-2 text-muted-foreground truncate max-w-[200px]" title={log.reason}>
                        {log.reason}
                      </td>
                      <td className="py-2 px-2 text-muted-foreground text-xs">
                        {log.enteredBy ? (
                          <span title={log.enteredBy.email}>
                            {log.enteredBy.name || log.enteredBy.email?.split('@')[0]}
                          </span>
                        ) : (
                          <span className="italic text-gray-400">Auto</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upcoming events */}
      {planning?.events?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Événements planifiés</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {planning.events.map(event => (
                <div key={event.id} className="flex items-center justify-between p-2 rounded bg-muted/30">
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-8 rounded ${getLeaveColor(event.type)}`} />
                    <div>
                      <p className="font-medium text-sm">{event.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {event.start === event.end 
                          ? formatDate(event.start)
                          : `${formatDate(event.start)} → ${formatDate(event.end)}`
                        }
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{event.days}j</Badge>
                    {isOwner && event.status !== 'TAKEN' && event.status !== 'CANCELLED' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs text-red-600 hover:bg-red-50 hover:text-red-700"
                        onClick={() => handleCancelEvent(event)}
                        data-testid={`cancel-event-${event.id}`}
                      >
                        <X className="w-3 h-3 mr-1" />
                        Annuler
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add Leave Dialog */}
      <Dialog open={showAddLeave} onOpenChange={setShowAddLeave}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter un congé ou absence</DialogTitle>
            <DialogDescription>
              Sélectionnez le type et les dates pour {employeeName || planning?.employee?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Type</Label>
              <Select value={leaveForm.type} onValueChange={(v) => setLeaveForm({...leaveForm, type: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEAVE_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded ${t.color}`} />
                        {t.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date début</Label>
                <Input 
                  type="date" 
                  value={leaveForm.start_date}
                  onChange={(e) => setLeaveForm({...leaveForm, start_date: e.target.value, end_date: leaveForm.end_date || e.target.value})}
                />
              </div>
              <div>
                <Label>Date fin</Label>
                <Input 
                  type="date" 
                  value={leaveForm.end_date}
                  onChange={(e) => setLeaveForm({...leaveForm, end_date: e.target.value})}
                />
              </div>
            </div>
            <div>
              <Label>Motif (optionnel)</Label>
              <Textarea 
                value={leaveForm.reason}
                onChange={(e) => setLeaveForm({...leaveForm, reason: e.target.value})}
                rows={2}
                placeholder="Ex: Mariage familial..."
              />
            </div>
            {leaveForm.type === 'CONGE' && (
              <p className="text-sm text-muted-foreground bg-green-50 p-2 rounded">
                Solde disponible: <strong>{safeFixed(planning?.balances?.leave, 1)} jours</strong>
              </p>
            )}
            {leaveForm.type === 'RECUP' && (
              <p className="text-sm text-muted-foreground bg-amber-50 p-2 rounded">
                Récupération disponible: <strong>{safeFixed(planning?.balances?.recovery, 1)} heures</strong>
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddLeave(false)}>Annuler</Button>
            <Button onClick={handleAddLeave} data-testid="confirm-add-leave">Ajouter</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Rest Day Dialog */}
      <Dialog open={showQuickRest} onOpenChange={setShowQuickRest}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Poser le jour de repos</DialogTitle>
            <DialogDescription>
              Semaine du {selectedWeek && formatDate(selectedWeek.start)} au {selectedWeek && formatDate(selectedWeek.end)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Choisissez le jour de repos :</p>
            <div className="grid grid-cols-7 gap-1">
              {selectedWeek?.days.map((day) => (
                <Button
                  key={day.dateStr}
                  variant={day.events.length > 0 ? "ghost" : "outline"}
                  disabled={day.events.length > 0}
                  className="h-14 flex flex-col p-1"
                  onClick={() => handleQuickAddRest(selectedWeek.startStr, day.dateStr)}
                >
                  <span className="text-xs text-muted-foreground">
                    {['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'][day.date.getDay()]}
                  </span>
                  <span className="font-bold">{day.date.getDate()}</span>
                </Button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQuickRest(false)}>Annuler</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credit Dialog */}
      <Dialog open={showCreditDialog} onOpenChange={setShowCreditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter un crédit manuel</DialogTitle>
            <DialogDescription>
              Créditez le solde de congés, repos ou récupération de {employeeName || planning?.employee?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Type de solde</Label>
              <Select value={creditForm.balance_type} onValueChange={(v) => setCreditForm({...creditForm, balance_type: v})}>
                <SelectTrigger data-testid="credit-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="LEAVE">
                    <div className="flex items-center gap-2">
                      <Umbrella className="h-4 w-4 text-green-600" />
                      Congés payés (jours)
                    </div>
                  </SelectItem>
                  <SelectItem value="REST">
                    <div className="flex items-center gap-2">
                      <Coffee className="h-4 w-4 text-blue-600" />
                      Repos à poser (jours)
                    </div>
                  </SelectItem>
                  <SelectItem value="RECOVERY">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-amber-600" />
                      Récupération (heures)
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Montant</Label>
              <Input 
                type="number" 
                min="0.5"
                step="0.5"
                value={creditForm.amount}
                onChange={(e) => setCreditForm({...creditForm, amount: parseFloat(e.target.value)})}
                data-testid="credit-amount-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {creditForm.balance_type === 'RECOVERY' ? 'Heures à créditer' : 'Jours à créditer'}
              </p>
            </div>
            <div>
              <Label>Raison <span className="text-red-500">*</span></Label>
              <Textarea 
                value={creditForm.reason}
                onChange={(e) => setCreditForm({...creditForm, reason: e.target.value})}
                rows={2}
                placeholder="Ex: Crédit mensuel congés, Repos hebdo non pris semaine 48..."
                data-testid="credit-reason-input"
              />
            </div>
            <div>
              <Label>Date de référence (optionnel)</Label>
              <Input 
                type="date" 
                value={creditForm.reference_date}
                onChange={(e) => setCreditForm({...creditForm, reference_date: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreditDialog(false)}>Annuler</Button>
            <Button onClick={handleAddCredit} disabled={!creditForm.reason} data-testid="confirm-add-credit">
              Créditer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rest Date Picker Dialog */}
      <Dialog open={showRestDatePicker} onOpenChange={setShowRestDatePicker}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Coffee className="h-5 w-5 text-blue-600" />
              Poser un jour de repos
            </DialogTitle>
            <DialogDescription>
              Choisissez la date sur laquelle poser le repos
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Date du repos</Label>
              <Input 
                type="date" 
                value={restDate}
                onChange={(e) => setRestDate(e.target.value)}
                data-testid="rest-date-input"
              />
            </div>
            <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-700">
              <p>Solde de repos à poser: <strong>{planning?.balances?.restDaysOwed || 0} jour(s)</strong></p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRestDatePicker(false)}>Annuler</Button>
            <Button 
              onClick={() => {
                handleRequestRest(restDate, 'REPOS');
                setShowRestDatePicker(false);
              }}
              data-testid="confirm-rest-date"
            >
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
